import { useEffect, useMemo, useState } from 'react'
import i18n from 'i18next'
import { Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useLlmChatSessionsList } from '@/shared/hooks/api/llmChat'
import { useListProducts, type LexProduct } from '@/shared/hooks/api/product'
import { Button } from '@/components/ui/button'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { ThemeSwitch } from '@/components/theme-switch'
import { AddProductDialog } from '../components/AddProductDialog'
import { ProductCatalogCard } from '../components/ProductCatalogCard'
import { ProductCatalogCardSkeleton } from '../components/ProductCatalogCardSkeleton'
import { ProductCatalogEmpty } from '../components/ProductCatalogEmpty'
import { ProductDeleteDialog } from '../components/ProductDeleteDialog'

const PRODUCT_CATALOG_NAMESPACES = ['productCatalog'] as const
const PRODUCT_LIST_SKELETON_COUNT = 5

type ProductWithLinkedChatCount = LexProduct & {
  linkedChatCount: number
}

export function ProductCatalogListPage() {
  const navigate = useNavigate()
  const { t } = useTranslation('productCatalog')
  const { data: products = [], isSuccess, isLoading } = useListProducts()
  const { data: chatSessionsData } = useLlmChatSessionsList()
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [productToDelete, setProductToDelete] =
    useState<ProductWithLinkedChatCount | null>(null)

  const hasProducts = isSuccess && products.length > 0
  const showProductListSkeleton = isLoading
  const showProductList = !isLoading && hasProducts
  const showEmptyState = !isLoading && !hasProducts
  const deleteTarget = productToDelete
  const showDeleteDialog = deleteTarget !== null

  const productsWithLinkedChatCount = useMemo<
    ProductWithLinkedChatCount[]
  >(() => {
    const counts = new Map<string, number>()
    for (const session of chatSessionsData?.sessions ?? []) {
      if (session.productId === null) {
        continue
      }
      counts.set(session.productId, (counts.get(session.productId) ?? 0) + 1)
    }

    return products.map((product) => ({
      ...product,
      linkedChatCount: counts.get(product.id) ?? 0,
    }))
  }, [chatSessionsData, products])

  useEffect(() => {
    void i18n.loadNamespaces([...PRODUCT_CATALOG_NAMESPACES])
  }, [])

  return (
    <>
      <Header fixed>
        <div className='ms-auto flex items-center space-x-4'>
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>

      <Main className='flex flex-1 flex-col gap-4 sm:gap-6'>
        <div className='space-y-4'>
          <header className='flex flex-wrap items-center justify-between gap-2'>
            <div>
              <h2 className='text-2xl font-bold tracking-tight'>
                {t('title')}
              </h2>
              <p className='text-muted-foreground'>{t('subtitle')}</p>
            </div>
            <Button
              className='gap-2'
              onClick={() => setAddDialogOpen(true)}
              type='button'
            >
              <Plus className='size-4' />
              {t('list.addButton')}
            </Button>
          </header>

          {showProductListSkeleton && (
            <ul
              className='space-y-2'
              aria-busy='true'
              aria-label={t('common:loading')}
            >
              {Array.from(
                { length: PRODUCT_LIST_SKELETON_COUNT },
                (_, index) => (
                  <li key={index}>
                    <ProductCatalogCardSkeleton />
                  </li>
                )
              )}
            </ul>
          )}

          {showProductList && (
            <ul className='space-y-2'>
              {productsWithLinkedChatCount.map((product) => (
                <li key={product.id}>
                  <ProductCatalogCard
                    product={product}
                    onDeleteClick={() => setProductToDelete(product)}
                  />
                </li>
              ))}
            </ul>
          )}

          {showEmptyState && (
            <ProductCatalogEmpty onAddClick={() => setAddDialogOpen(true)} />
          )}
        </div>
      </Main>

      <AddProductDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        onCreated={(productId) => navigate(`/product-catalog/${productId}`)}
      />
      {showDeleteDialog && (
        <ProductDeleteDialog
          linkedChatCount={deleteTarget.linkedChatCount}
          open={showDeleteDialog}
          onOpenChange={(open) => {
            if (!open) {
              setProductToDelete(null)
            }
          }}
          product={deleteTarget}
          onDeleted={() => setProductToDelete(null)}
        />
      )}
    </>
  )
}
